import React, { useEffect, useState } from 'react';
import {
  Grid,
  Typography,
  Button,
  Card,
  IconButton,
  Box,
  Stack,
  darken,
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  HeadsetMicOutlined as HeadsetMicOutlinedIcon,
} from '@mui/icons-material';

import { useDispatch } from 'react-redux';
import moment, { Moment } from 'moment';
import { TP_COLORS } from '../../../../constants';
import { useAppSelector } from '../../../../hooks';

import {
  getTutorAvailabilitySlotsForProfile,
  getUserAvailabilityState,
  TUserAvailabilitySlotOption,
} from '../../../../store/V2/UserAvailabilitySlice';
import { getAuthState } from '../../../../store/AuthSlice';
import { getTutorsState } from '../../../../store/V2/TutorSlice';
import { createTrial } from '../../../../store/V2/TrialsSlice';

import InputSection from './InputSection';
import SignUpModal from '../../../Authentication/SignUpModal';
import BasicLoader from '../../../../components/Loaders/BasicLoader';
import LoginModal from '../../../Authentication/LoginModal';

const DAYS = 7;

const getDates = (startDate: Moment) => {
  return Array.from({ length: DAYS }, (_, index) =>
    startDate.clone().add(index, 'days')
  );
};

type TSelectOption = {
  label: string;
  value: string;
};

export type TTrialState = {
  selectedCurriculum: TSelectOption | null;
  selectedSubject: TSelectOption | null;
  selectedGrade: TSelectOption | null;
  selectedSlot?: TUserAvailabilitySlotOption | undefined;
};

const initialState: TTrialState = {
  selectedCurriculum: null,
  selectedSubject: null,
  selectedGrade: null,
  selectedSlot: undefined,
};

const Calendar: React.FC = () => {
  const dispatch = useDispatch();

  // Store
  const { user: authUser } = useAppSelector(getAuthState);
  const { tutorData } = useAppSelector(getTutorsState);
  const { userAvailabilitySlotOptionsForTrial, status } = useAppSelector(
    getUserAvailabilityState
  );

  // State
  const [signUpModalOpen, setSignUpModalOpen] = useState<boolean>(false);
  const [loginModalOpen, setLoginModalOpen] = useState<boolean>(false);
  const [state, setState] = useState(initialState);
  const [isFetched, setIsFetched] = useState(false);
  const [weekStartDate, setWeekStartDate] = useState<Moment>(moment());
  const [dates, setDates] = useState<Moment[]>(getDates(moment()));
  const [hasError, setHasError] = useState(false);

  const isSubmitDisabled = !state.selectedSlot || status === 'loading';

  useEffect(() => {
    if (tutorData) {
      dispatch(
        getTutorAvailabilitySlotsForProfile({
          tutor: tutorData._id,
        })
      );
    }
  }, [tutorData?._id]);

  useEffect(() => {
    if (!isFetched && userAvailabilitySlotOptionsForTrial?.length) {
      const firstAvailableDate = moment(
        userAvailabilitySlotOptionsForTrial[0].start
      );

      setWeekStartDate(firstAvailableDate);
      setDates(getDates(firstAvailableDate));
      setIsFetched(true);
    }
  }, [userAvailabilitySlotOptionsForTrial]);

  const handleClickPrev = () => {
    const prevWeekStart = weekStartDate.clone().subtract(DAYS, 'days');
    const currentWeekStart = moment().startOf('week');

    if (prevWeekStart.isBefore(currentWeekStart)) {
      return;
    }

    if (tutorData) {
      dispatch(
        getTutorAvailabilitySlotsForProfile({
          date: prevWeekStart.toISOString(),
          tutor: tutorData._id,
        })
      );
    }
    setWeekStartDate(prevWeekStart);
    setDates(getDates(prevWeekStart));
  };

  const handleClickNext = () => {
    const nextWeekStart = weekStartDate.clone().add(DAYS, 'days');

    if (tutorData) {
      dispatch(
        getTutorAvailabilitySlotsForProfile({
          date: nextWeekStart.toISOString(),
          tutor: tutorData._id,
        })
      );
    }
    setWeekStartDate(nextWeekStart);
    setDates(getDates(nextWeekStart));
  };

  const handleBookTrialClass = () => {
    if (
      tutorData &&
      state.selectedCurriculum &&
      state.selectedSubject &&
      state.selectedGrade &&
      state.selectedSlot
    ) {
      if (authUser && !authUser.role.isGuest) {
        dispatch(
          createTrial({
            curriculum: state.selectedCurriculum.value,
            subject: state.selectedSubject.value,
            grade: state.selectedGrade.value,
            startDate: moment(state.selectedSlot.start).toISOString(),
            endDate: state.selectedSlot.end,
            tutorId: tutorData._id,
          })
        );
      } else {
        setSignUpModalOpen(true);
      }
    } else {
      setHasError(true);
    }
  };

  const handleHelpClick = () => {
    let message = 'Hello! I would like to create a';

    if (state.selectedCurriculum) {
      message += ` ${state.selectedCurriculum.value}`;
    }

    if (state.selectedSubject) {
      message += ` ${state.selectedSubject.value}`;
    }

    message += ' trial class';

    if (state.selectedGrade) {
      message += ` for ${state.selectedGrade.value}`;
    }

    if (tutorData) {
      message += ` with ${tutorData.firstname} ${tutorData.lastname || ''}`;
    }

    window.open(
      `${
        process.env.REACT_APP_WHATS_APP_SUPPORT_BASE_URL
      }?text=${encodeURIComponent(message)}`,
      '_blank'
    );
  };

  const updateStateAttributes = (attributes: Partial<TTrialState>) => {
    setState((prevState) => {
      const newState = {
        ...prevState,
        ...attributes,
      };
      if (
        newState.selectedCurriculum &&
        newState.selectedSubject &&
        newState.selectedGrade
      ) {
        setHasError(false);
      }
      return newState;
    });
  };

  return (
    <Card
      sx={{
        padding: '20px',
        borderRadius: '30px',
        border: '1px solid #E2E8F0',
        boxShadow: '0px 14px 40px 0px #6E9DB22E',
      }}
    >
      <InputSection
        state={state}
        updateStateAttributes={updateStateAttributes}
      />

      <Grid container alignItems="center">
        <IconButton
          onClick={handleClickPrev}
          disabled={weekStartDate.isBefore(moment().startOf('week'))}
        >
          <ChevronLeftIcon />
        </IconButton>
        <IconButton sx={{ marginRight: '16px' }} onClick={handleClickNext}>
          <ChevronRightIcon />
        </IconButton>
        <Typography fontSize="16px" fontWeight="600">
          {weekStartDate.format('MMM DD')} -{' '}
          {weekStartDate
            .clone()
            .add(DAYS - 1, 'days')
            .format('DD')}
          <span
            style={{
              color: '#6B7280',
              fontSize: '14px',
              fontWeight: '500',
              marginLeft: '4px',
            }}
          >
            {weekStartDate.format('YYYY')}
          </span>
        </Typography>
      </Grid>

      <Box
        sx={{
          borderRadius: '10px',
          backgroundColor: TP_COLORS.listItemGray.light,
          marginTop: '22px',
        }}
      >
        <Grid container>
          {dates.map((day, index) => {
            const isToday = day.isSame(moment(), 'day');
            return (
              <Grid item xs={1.7} key={index}>
                <Typography
                  textAlign="center"
                  fontWeight={isToday ? '600' : '500'}
                  sx={{
                    padding: '8px 0',
                    borderBottom: isToday ? '2px solid black' : 'none',
                  }}
                >
                  {day.format('ddd')} <br /> {day.format('DD')}
                </Typography>
              </Grid>
            );
          })}
        </Grid>
      </Box>

      {/* Time Slots */}
      <Grid
        container
        columnSpacing="10px"
        sx={{ marginTop: 2, minHeight: '200px' }}
      >
        {status !== 'loading' ? (
          dates.map((day, dayIndex) => {
            const filtered =
              userAvailabilitySlotOptionsForTrial?.filter((item) =>
                day.isSame(moment(item.start), 'day')
              ) || [];

            return (
              <Grid item xs={1.7} key={dayIndex}>
                {filtered.map((slotItem, slotIndex) => {
                  const isSelected = state.selectedSlot === slotItem;
                  const background = isSelected
                    ? TP_COLORS.gray.darken
                    : TP_COLORS.listItemGray.light;
                  return (
                    <Button
                      fullWidth
                      disableElevation
                      size="small"
                      key={slotIndex}
                      variant="contained"
                      color="secondary"
                      sx={{
                        minWidth: '35px',
                        padding: '4px',
                        marginBottom: '5px',
                        backgroundColor: background,
                        color: isSelected ? '#F8FAFC' : TP_COLORS.gray.darken,
                        '&:hover': {
                          backgroundColor: darken(background, 0.2),
                        },
                      }}
                      onClick={() =>
                        updateStateAttributes({
                          selectedSlot:
                            state.selectedSlot === slotItem
                              ? undefined
                              : slotItem,
                        })
                      }
                    >
                      {moment(slotItem.start).format('HH:mm')}
                    </Button>
                  );
                })}
              </Grid>
            );
          })
        ) : (
          <Grid item xs={12}>
            <BasicLoader
              containerProps={{
                height: '100%',
                alignItems: 'center',
              }}
            />
          </Grid>
        )}

        {status !== 'loading' &&
          userAvailabilitySlotOptionsForTrial?.length === 0 && (
            <Grid item xs={12}>
              <Stack alignItems="center">
                <Typography>No slots available</Typography>
              </Stack>
            </Grid>
          )}
      </Grid>

      <Stack spacing="10px" margin="10px 10px 0">
        <Typography
          color="#e74c3c"
          fontSize="14px"
          visibility={hasError ? 'visible' : 'hidden'}
        >
          *Select your curriculum, subject, and grade
        </Typography>

        <Button
          fullWidth
          disableElevation
          variant="contained"
          color="secondary"
          sx={{
            padding: '10px',
            fontWeight: '600',
            borderRadius: '8px',
            backgroundColor: TP_COLORS.gray.darken,
          }}
          disabled={isSubmitDisabled}
          onClick={handleBookTrialClass}
        >
          Book FREE Trial
        </Button>

        <Button
          fullWidth
          disableElevation
          color="secondary"
          onClick={handleHelpClick}
          sx={{
            fontWeight: '600',
            borderRadius: '8px',
            color: TP_COLORS.gray.darken,
            textDecoration: 'underline',
          }}
          startIcon={
            <HeadsetMicOutlinedIcon sx={{ width: '18px', height: '18px' }} />
          }
        >
          Need help?
        </Button>
      </Stack>
      {/* Modals */}
      <SignUpModal
        open={signUpModalOpen}
        trialState={state}
        onClose={() => setSignUpModalOpen(false)}
        onLoginClick={() => setLoginModalOpen(true)}
      />
      <LoginModal
        open={loginModalOpen}
        onClose={() => setLoginModalOpen(false)}
        onSignUpClick={() => setSignUpModalOpen(true)}
      />
    </Card>
  );
};

export default Calendar;
